<template>
  <div class="p-auth">
    <aside class="p-auth__art">
      <div class="p-auth__brand">
        <img src="/favicon.png" alt="Pineapple" class="p-auth__logo" />
        <div class="p-auth__brand-text">
          <div class="p-auth__brand-name">Pineapple</div>
          <div class="p-auth__brand-sub">EDITOR PORTAL</div>
        </div>
      </div>
      <div class="p-auth__pine">
        <PineCharacter :size="220" />
        <div class="p-auth__editor-badge">
          <i class="material-icons-round">edit_note</i>
          <span>FOR EDITORS</span>
        </div>
      </div>
      <p class="p-auth__tagline">
        콘텐츠를 만드는 <strong>에디터</strong>를 위한<br />
        <strong>파인애플 아카데미</strong> 작업실
      </p>
    </aside>

    <section class="p-auth__panel">
      <div class="p-auth__form">
        <header class="p-auth__head">
          <span class="p-auth__role-badge">
            <i class="material-icons-round">verified</i>
            FOR {{ $CONF.APP_USERTYPE }}
          </span>
          <h1 class="p-auth__head-title">에디터 로그인</h1>
          <p class="p-auth__head-sub">파인애플 콘텐츠 에디터 전용 작업실입니다</p>
        </header>

        <VeeForm
          :validation-schema="schema"
          class="p-auth__veeform"
          @submit="onSubmit"
        >
          <Field v-slot="{ field, errors }" name="email">
            <div class="p-field">
              <label class="p-field__label" for="auth-email">
                <i class="material-icons-round p-field__label-icon">mail</i>
                이메일 <span class="p-field__req">*</span>
              </label>
              <input
                id="auth-email"
                v-bind="field"
                type="email"
                autocomplete="username"
                placeholder="name@example.com"
                class="p-input"
                :class="{ 'is-error': errors.length > 0 }"
              />
              <ErrorMessage name="email" v-slot="{ message }">
                <div class="p-field__error">{{ message }}</div>
              </ErrorMessage>
            </div>
          </Field>

          <Field v-slot="{ field, errors }" name="password">
            <div class="p-field">
              <label class="p-field__label" for="auth-password">
                <i class="material-icons-round p-field__label-icon">lock</i>
                비밀번호 <span class="p-field__req">*</span>
              </label>
              <input
                id="auth-password"
                v-bind="field"
                type="password"
                autocomplete="current-password"
                placeholder="••••••••"
                class="p-input"
                :class="{ 'is-error': errors.length > 0 }"
              />
              <ErrorMessage name="password" v-slot="{ message }">
                <div class="p-field__error">{{ message }}</div>
              </ErrorMessage>
            </div>
          </Field>

          <ABtn
            type="submit"
            kind="primary"
            block
            icon="login"
            class="p-auth__submit"
          >Sign in</ABtn>
        </VeeForm>

        <p class="p-auth__footer">
          © {{ new Date().getFullYear() }} Pineapple Academy
        </p>
      </div>
    </section>
  </div>
</template>

<script>
import { mapMutations } from "vuex";
import { Field, Form as VeeForm, ErrorMessage } from "vee-validate";
import * as yup from "yup";
import ABtn from "@/components/Pineapple/ABtn.vue";
import PineCharacter from "@/components/Pineapple/PineCharacter.vue";

export default {
  name: "SigninCover",
  components: {
    Field,
    VeeForm,
    ErrorMessage,
    ABtn,
    PineCharacter,
  },
  data() {
    return {
      schema: yup.object({
        email: yup.string().email("올바른 이메일 형식이 아닙니다").required("이메일을 입력해주세요"),
        password: yup
          .string()
          .min(6, "비밀번호는 6자 이상이어야 합니다")
          .required("비밀번호를 입력해주세요"),
      }),
    };
  },
  beforeMount() {
    this.toggleEveryDisplay();
    this.toggleHideConfig();
  },
  beforeUnmount() {
    this.toggleEveryDisplay();
    this.toggleHideConfig();
  },
  methods: {
    ...mapMutations(["toggleEveryDisplay", "toggleHideConfig"]),
    onSubmit(values) {
      this.$store.dispatch(this.$CONF.ACT_LOGIN, values);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.p-auth {
  min-height: 100vh;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  background: $p-bg;
  font-family: $p-font-body;
}

.p-auth__art {
  background: linear-gradient(160deg, $p-point-soft 0%, $p-bg 60%, $p-panel-alt 100%);
  padding: 56px 64px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 32px;
  position: relative;
  overflow: hidden;

  // Decorative dots
  &::before, &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    pointer-events: none;
  }
  &::before {
    width: 320px; height: 320px;
    top: -120px; right: -120px;
    background: radial-gradient(circle, rgba(255, 201, 60, 0.25) 0%, transparent 70%);
  }
  &::after {
    width: 240px; height: 240px;
    bottom: -100px; left: -60px;
    background: radial-gradient(circle, rgba(30, 140, 102, 0.18) 0%, transparent 70%);
  }
}

.p-auth__brand {
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
  z-index: 1;
}

.p-auth__logo {
  width: 40px;
  height: 40px;
  object-fit: contain;
}

.p-auth__brand-name {
  font-size: 18px;
  font-weight: 800;
  color: $p-ink;
  line-height: 1.1;
}

.p-auth__brand-sub {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.14em;
  color: $p-point-deep;
  margin-top: 3px;
}

.p-auth__pine {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  position: relative;
  z-index: 1;
}

.p-auth__editor-badge {
  position: absolute;
  top: 50%;
  right: 8%;
  transform: translateY(-40%) rotate(8deg);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: $p-ink;
  color: $p-point;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 900;
  letter-spacing: 0.18em;
  box-shadow: 0 10px 24px -6px rgba($p-ink, 0.35), 0 0 0 3px $p-point inset;
  animation: pAuthFloat 3.2s ease-in-out infinite;

  i {
    font-size: 18px;
    color: $p-point;
  }
}

@keyframes pAuthFloat {
  0%, 100% { transform: translateY(-40%) rotate(8deg); }
  50%       { transform: translateY(-50%) rotate(8deg); }
}

.p-auth__tagline {
  margin: 0;
  font-size: 16px;
  color: $p-ink2;
  line-height: 1.5;
  position: relative;
  z-index: 1;

  strong {
    color: $p-point-deep;
    font-weight: 800;
  }
}

.p-auth__panel {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  background: $p-panel;
}

.p-auth__form {
  width: 100%;
  max-width: 380px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.p-auth__head {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.p-auth__role-badge {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px 6px 8px;
  background: linear-gradient(135deg, $p-point 0%, lighten($p-point, 6%) 100%);
  color: $p-ink;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.18em;
  box-shadow: 0 4px 12px -4px rgba($p-point, 0.5);
  margin-bottom: 10px;
  text-transform: uppercase;

  i {
    font-size: 16px;
    color: $p-ink;
  }
}

.p-auth__head-title {
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  color: $p-ink;
  letter-spacing: -0.01em;
}

.p-auth__head-sub {
  margin: 4px 0 0;
  font-size: 13px;
  color: $p-ink-mid;
}

.p-auth__veeform {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.p-auth__submit {
  margin-top: 8px;
  height: 42px !important;
}

.p-auth__footer {
  margin: 16px 0 0;
  text-align: center;
  font-size: 11px;
  color: $p-ink-soft;
}

@media (max-width: 960px) {
  .p-auth { grid-template-columns: 1fr; }
  .p-auth__art {
    padding: 32px;
    min-height: 280px;
  }
  .p-auth__panel { padding: 32px 20px; }
}
</style>
